import {
  LoaderCircle,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  UsersRound,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import ClienteModal from '../components/clientes/ClienteModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'
import { formatearRut, limpiarRut } from '../utils/rut'

function Clientes() {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const esConsulta = empresaActual?.rol === 'consulta'

  const [clientes, setClientes] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalAbierto, setModalAbierto] = useState(false)
  const [clienteEditar, setClienteEditar] = useState(null)
  const [actualizandoId, setActualizandoId] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    cerrarModalCliente()

    if (!empresaActual?.id) {
      setClientes([])
      setCargando(false)
      return
    }

    cargarClientes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarClientes = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('clientes')
        .select(`
          id,
          empresa_id,
          rut,
          razon_social,
          giro,
          direccion,
          comuna,
          ciudad,
          telefono,
          correo,
          contacto,
          activo,
          creado_en,
          actualizado_en
        `)
        .eq('empresa_id', empresaActual.id)
        .order('razon_social', { ascending: true })

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setClientes(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar clientes:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar los clientes.'
      )
      setClientes([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const clientesFiltrados = clientes.filter((cliente) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    const nombreCoincide = (cliente.razon_social || '')
      .toLowerCase()
      .includes(termino)

    const correoCoincide = (cliente.correo || '')
      .toLowerCase()
      .includes(termino)

    const contactoCoincide = (cliente.contacto || '')
      .toLowerCase()
      .includes(termino)

    const rutCoincide = limpiarRut(cliente.rut).includes(
      limpiarRut(busqueda)
    )

    return (
      nombreCoincide ||
      correoCoincide ||
      contactoCoincide ||
      rutCoincide
    )
  })

  const abrirCreacion = () => {
    setClienteEditar(null)
    setModalAbierto(true)
  }

  const abrirEdicion = (cliente) => {
    setClienteEditar(cliente)
    setModalAbierto(true)
  }

  const cerrarModalCliente = () => {
    setModalAbierto(false)
    setClienteEditar(null)
  }

  const alGuardar = (clienteGuardado) => {
    const fueEdicion = Boolean(clienteEditar)

    cerrarModalCliente()
    cargarClientes()

    abrirModal({
      tipo: 'success',
      titulo: fueEdicion ? 'Cliente actualizado' : 'Cliente creado',
      mensaje: `El cliente "${clienteGuardado?.razon_social}" fue ${
        fueEdicion ? 'actualizado' : 'creado'
      } correctamente.`,
    })
  }

  const aplicarCambioEstado = async (
    cliente,
    activar,
    esDesactivacion
  ) => {
    if (esDesactivacion) {
      actualizarModal({
        procesando: true,
        mostrarCancelar: false,
        textoAceptar: 'Desactivando...',
      })
    }

    try {
      setActualizandoId(cliente.id)

      const { error: errorActualizar } = await supabase
        .from('clientes')
        .update({ activo: activar })
        .eq('id', cliente.id)
        .eq('empresa_id', empresaActual.id)

      if (errorActualizar) {
        throw errorActualizar
      }

      if (!montadoRef.current) return

      await cargarClientes()

      if (esDesactivacion) {
        abrirModal({
          tipo: 'success',
          titulo: 'Cliente desactivado',
          mensaje: 'El cliente fue desactivado correctamente.',
        })
      }
    } catch (errorEstado) {
      console.error(
        'Error al actualizar estado del cliente:',
        errorEstado
      )

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar',
        mensaje:
          errorEstado.message ||
          'No fue posible actualizar el estado del cliente.',
      })
    } finally {
      if (montadoRef.current) {
        setActualizandoId(null)
      }
    }
  }

  const cambiarEstado = (cliente) => {
    const activar = !cliente.activo

    if (!activar) {
      abrirModal({
        tipo: 'confirm',
        titulo: 'Desactivar cliente',
        mensaje: `¿Deseas desactivar a "${cliente.razon_social}"?`,
        textoAceptar: 'Desactivar',
        textoCancelar: 'Cancelar',
        mostrarCancelar: true,
        onAceptar: () =>
          aplicarCambioEstado(cliente, false, true),
      })
      return
    }

    aplicarCambioEstado(cliente, true, false)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando clientes...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarClientes}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (clientes.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <UsersRound
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay clientes registrados
          </h3>

          <p className="mt-2 text-slate-500">
            {esConsulta
              ? 'Aún no se han registrado clientes.'
              : 'Presiona “Nuevo cliente” para ingresar el primer registro.'}
          </p>

          {!esConsulta && (
            <button
              type="button"
              onClick={abrirCreacion}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <Plus size={19} />
              Ingresar primer cliente
            </button>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por nombre, RUT, correo o contacto"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {clientesFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron clientes para “{busqueda}”.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[860px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Nombre</th>
                  <th className="px-6 py-3">RUT</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Contacto
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Correo
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Teléfono
                  </th>
                  <th className="px-6 py-3">Estado</th>
                  {!esConsulta && (
                    <th className="px-6 py-3 text-right">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {clientesFiltrados.map((cliente) => (
                  <tr key={cliente.id}>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {cliente.razon_social}
                    </td>

                    <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                      {cliente.rut
                        ? formatearRut(cliente.rut)
                        : '—'}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                      {cliente.contacto || '—'}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 md:table-cell">
                      {cliente.correo || '—'}
                    </td>

                    <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                      {cliente.telefono || '—'}
                    </td>

                    <td className="px-6 py-4">
                      {cliente.activo ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                          Inactivo
                        </span>
                      )}
                    </td>

                    {!esConsulta && (
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              abrirEdicion(cliente)
                            }
                            title="Editar"
                            aria-label="Editar cliente"
                            className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                          >
                            <Pencil size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              cambiarEstado(cliente)
                            }
                            disabled={
                              actualizandoId === cliente.id
                            }
                            title={
                              cliente.activo
                                ? 'Desactivar'
                                : 'Reactivar'
                            }
                            aria-label={
                              cliente.activo
                                ? 'Desactivar cliente'
                                : 'Reactivar cliente'
                            }
                            className={
                              cliente.activo
                                ? 'rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
                                : 'rounded-lg border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                            }
                          >
                            {actualizandoId === cliente.id ? (
                              <LoaderCircle
                                size={16}
                                className="animate-spin"
                              />
                            ) : cliente.activo ? (
                              <PowerOff size={16} />
                            ) : (
                              <Power size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Mantenedores
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Clientes
          </h2>

          <p className="mt-2 text-slate-500">
            Administra los clientes de la empresa seleccionada.
          </p>
        </div>

        {!esConsulta && (
          <button
            type="button"
            onClick={abrirCreacion}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nuevo cliente
          </button>
        )}
      </div>

      {renderContenido()}

      {modalAbierto && (
        <ClienteModal
          cliente={clienteEditar}
          onClose={cerrarModalCliente}
          onGuardado={alGuardar}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default Clientes
